import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Menu, MenuItem, Chip, Badge, Avatar, Divider, ListItemIcon, ListItemText } from '@mui/material';
import { Logout, ExpandMore, Notifications as NotificationsIcon, AccountCircle } from '@mui/icons-material';
import usePermissions from '../../hooks/usePermissions';
import permissionService from '../../services/permissionService';
import useJourneyProgress from '../../hooks/useJourneyProgress';
import api from '../../config/axiosConfig';
import useConemVersion from '../../hooks/useConemVersion';
import OfflineSyncIndicator from '../common/OfflineSyncIndicator';
import { OFFLINE_FIELD_MODE } from '../../config/features';

const Navbar = ({ onLogout }) => {
  const conemVersion = useConemVersion();
  console.log('🏗️ Navbar component renderizándose - pathname:', window.location.pathname);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, loading: permissionsLoading, permissions } = usePermissions();
  const { hasAcceptedQuotation, hasCompletedInfo, hasUploadedFiles } = useJourneyProgress();
  const previousMainSection = useRef(null);
  
  console.log('🔍 Estado de permisos al renderizar:', {
    loading: permissionsLoading,
    permissionsCount: permissions?.length || 0,
    hasPermissions: !!permissions,
    permissionsArray: permissions
  });
  
  // Debug logging detallado
  console.log('🔍 Navbar - Estado completo de permisos:', {
    loading: permissionsLoading,
    permissionsCount: permissions?.length || 0,
    permissions: permissions,
    hasPermissionFunction: typeof hasPermission,
    timestamp: new Date().toLocaleTimeString('es-MX')
  });

  // DEBUG SIMPLE: Verificar algunos submenús clave
  if (!permissionsLoading && permissions && permissions.length > 0) {
    console.log('🔍 Verificando submenús de Nosotros:');
    ['/nosotros/empresa', '/nosotros/historia', '/nosotros/equipo'].forEach(route => {
      const access = hasPermission(route, 'read');
      console.log(`  ${route}: ${access ? '✅ MOSTRAR' : '❌ OCULTAR'}`);
    });
  }

  // Detectar cambios de menú principal y recargar permisos
  useEffect(() => {
    const getMainSection = (pathname) => {
      const parts = pathname.split('/').filter(Boolean);
      return parts[0] || 'home';
    };

    const currentMainSection = getMainSection(location.pathname);
    
    console.log('🔄 Navbar - Cambio de ruta detectado:', {
      pathname: location.pathname,
      currentMainSection,
      previousMainSection: previousMainSection.current
    });

    // Solo recargar si cambió la sección principal (no la primera vez)
    if (previousMainSection.current && previousMainSection.current !== currentMainSection) {
      console.log('🔄 Navbar - CAMBIO DE MENÚ PRINCIPAL detectado:', {
        from: previousMainSection.current,
        to: currentMainSection
      });
      
      const token = sessionStorage.getItem('authToken');
      if (token) {
        console.log('🚀 Navbar - Recargando permisos por cambio de menú principal...');
        reloadPermissions();
      } else {
        console.log('❌ Navbar - No hay token para recargar permisos');
      }
    }

    // Actualizar la referencia
    previousMainSection.current = currentMainSection;
  }, [location.pathname]);

  // Función para recargar permisos
  const reloadPermissions = async () => {
    try {
      console.log('🔄 Navbar - Ejecutando recarga de permisos...');
      const userPermissions = await permissionService.getUserPermissions();
      console.log('✅ Navbar - Permisos recargados exitosamente:', {
        screens: userPermissions.screens?.length || 0,
        role: userPermissions.role?.name
      });
      
      // Disparar evento para que usePermissions se actualice
      window.dispatchEvent(new CustomEvent('permissionsRefreshed', {
        detail: {
          permissions: userPermissions,
          timestamp: new Date().toISOString(),
          trigger: 'menu-change'
        }
      }));
    } catch (error) {
      console.error('❌ Navbar - Error recargando permisos:', error);
    }
  };

  // Debug: Mostrar información COMPLETA de permisos cuando cambien
  React.useEffect(() => {
    // Obtener información del usuario desde localStorage
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
    
    console.log('🔄 Navbar - Permisos actualizados:', {
      loading: permissionsLoading,
      count: permissions?.length || 0,
      hasPermissionFunc: typeof hasPermission,
      userRole: userInfo?.role?.name || 'DESCONOCIDO',
      userId: userInfo?.id || 'DESCONOCIDO',
      username: userInfo?.username || 'DESCONOCIDO'
    });
    
    console.log('🚨 DIAGNÓSTICO DE PERMISOS INVERTIDOS:');
    console.log('👤 Usuario actual:', userInfo?.role?.name || 'DESCONOCIDO');
    
    if (permissions && permissions.length > 0) {
      console.log('📋 PERMISOS COMPLETOS DEL BACKEND:');
      permissions.forEach((permission, index) => {
        console.log(`  ${index + 1}. ${permission.route || permission.screen} - canRead: ${permission.canRead}, canWrite: ${permission.canWrite}, canAdmin: ${permission.canAdmin}`);
      });
      
      // Verificar específicamente las rutas que nos interesan
      const routesToCheck = [
        '/', '/home', '/nosotros/empresa', '/nosotros/equipo', '/nosotros/contacto',
        '/clientes/empresas', '/clientes/sucursales', '/new-quotation', '/estadisticas',
        '/servicios/catalogo', '/servicios/agenda', '/servicios/programas',
        '/usuarios/catalogo', '/usuarios/roles', '/utilerias/correos', '/utilerias/dropbox'
      ];
      
      console.log('🔍 VERIFICACIÓN DE RUTAS ESPECÍFICAS:');
      routesToCheck.forEach(route => {
        const access = hasPermission(route, 'read');
        console.log(`  ${route}: ${access ? '✅ PERMITIDO' : '❌ DENEGADO'}`);
      });
      
      // Contar permisos por tipo
      const readCount = permissions.filter(p => p.canRead).length;
      const writeCount = permissions.filter(p => p.canWrite).length;
      const adminPermCount = permissions.filter(p => p.canAdmin).length;
      
      console.log('📊 RESUMEN DE PERMISOS:');
      console.log(`  Total pantallas: ${permissions.length}`);
      console.log(`  Con canRead=true: ${readCount}`);
      console.log(`  Con canWrite=true: ${writeCount}`);
      console.log(`  Con canAdmin=true: ${adminPermCount}`);
      
      if (readCount === 0) {
        console.log('⚠️ PROBLEMA: Usuario sin permisos de lectura en ninguna pantalla');
      }
      
      // DIAGNÓSTICO ESPECÍFICO PARA PROBLEMA DE PERMISOS INVERTIDOS
      console.log('🔍 ANÁLISIS DE PROBLEMA:');
      const expectedForInvitado = ['/', '/home', '/nosotros/empresa', '/nosotros/equipo', '/nosotros/contacto'];
      const expectedForAdmin = ['/clientes/empresas', '/new-quotation', '/servicios/catalogo', '/usuarios/catalogo'];
      
      const invitadoAccess = expectedForInvitado.map(route => ({
        route,
        hasAccess: hasPermission(route, 'read')
      }));
      
      const adminAccess = expectedForAdmin.map(route => ({
        route, 
        hasAccess: hasPermission(route, 'read')
      }));
      
      console.log('📊 Acceso a rutas de INVITADO:', invitadoAccess);
      console.log('📊 Acceso a rutas de ADMIN:', adminAccess);
      
      const invitadoCount = invitadoAccess.filter(r => r.hasAccess).length;
      const adminRouteCount = adminAccess.filter(r => r.hasAccess).length;
      
      console.log(`🎯 RESUMEN: ${invitadoCount}/5 rutas INVITADO, ${adminRouteCount}/4 rutas ADMIN`);
      
      if (userInfo?.role?.name === 'INVITADO' && adminRouteCount > 0) {
        console.log('🚨 PROBLEMA DETECTADO: Usuario INVITADO tiene acceso a rutas de ADMIN');
      }
      
      if (userInfo?.role?.name === 'ADMINISTRADOR' && invitadoCount > adminRouteCount) {
        console.log('🚨 PROBLEMA DETECTADO: Usuario ADMIN solo tiene acceso a rutas de INVITADO');
      }
      
    } else {
      console.log('❌ NO HAY PERMISOS DEL BACKEND');
    }
  }, [permissions, permissionsLoading, hasPermission]);
  
  const [unreadCount, setUnreadCount] = useState(0);
  // Cliente (portal): inmuebles con documentos liberados que aún no confirma de recibido
  const [pendingDeliveries, setPendingDeliveries] = useState(0);
  useEffect(() => {
    if (!isGuestUser()) return undefined;
    const load = () => api.get('/deliveries/mine')
      .then((res) => setPendingDeliveries((res.data?.entities || []).filter((e) => e.stage === 'LIBERADA').length))
      .catch(() => {});
    load();
    const interval = setInterval(() => { if (!document.hidden) load(); }, 120000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contador de notificaciones no leídas, visible en toda la app (antes solo se
  // veía dentro del Home, y solo se cargaba una vez al montar esa pantalla).
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
        if (!userInfo?.id) return;
        const response = await api.get(`/notifications/unread/count?userId=${userInfo.id}`);
        setUnreadCount(response.data?.count || 0);
      } catch (error) {
        console.error('Error obteniendo notificaciones no leídas:', error);
      }
    };
    fetchUnreadCount();
    // Con la pestaña oculta no se consulta (evita una petición por minuto de pestañas olvidadas);
    // al volver a ella se actualiza al instante.
    const interval = setInterval(() => { if (!document.hidden) fetchUnreadCount(); }, 60000);
    const handleVisibility = () => { if (!document.hidden) fetchUnreadCount(); };
    document.addEventListener('visibilitychange', handleVisibility);
    // Antes solo se enteraba de una notificación marcada como leída hasta el siguiente tick del
    // intervalo (hasta 60s de retraso, o requería F5). Home.js (y cualquier otra pantalla que
    // marque notificaciones como leídas) dispara este evento justo después de que el PUT al
    // backend confirma, para que la campanita se ponga al día al instante.
    window.addEventListener('conem:notifications-updated', fetchUnreadCount);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('conem:notifications-updated', fetchUnreadCount);
    };
  }, [location.pathname]);

  // Usuario conectado, mostrado en el avatar del banner. userInfo lo llena
  // permissionService (no está disponible aún en el primer render), así que se
  // relee cada vez que cambia la ruta -- igual que el contador de notificaciones.
  const [currentUserInfo, setCurrentUserInfo] = useState({});
  useEffect(() => {
    setCurrentUserInfo(JSON.parse(sessionStorage.getItem('userInfo') || '{}'));
  }, [location.pathname]);

  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const handleUserMenuOpen = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);

  const userDisplayName = [currentUserInfo.firstName, currentUserInfo.firstLastName]
    .filter(Boolean).join(' ') || currentUserInfo.username || 'Usuario';
  const userRoleName = currentUserInfo.role?.name || currentUserInfo.role || '';
  const userInitials = (
    (currentUserInfo.firstName?.[0] || '') + (currentUserInfo.firstLastName?.[0] || '')
  ).toUpperCase() || userDisplayName.charAt(0).toUpperCase();

  const [administracionAnchor, setAdministracionAnchor] = useState(null);
  const [cotizacionesAnchor, setCotizacionesAnchor] = useState(null);
  const [serviciosAnchor, setServiciosAnchor] = useState(null);
  const [clientesAnchor, setClientesAnchor] = useState(null);
  const [finanzasAnchor, setFinanzasAnchor] = useState(null);
  const [utileriasAnchor, setUtileriasAnchor] = useState(null);
  const [usuariosAnchor, setUsuariosAnchor] = useState(null);
  const [nosotrosAnchor, setNosotrosAnchor] = useState(null);
  const [perfilAnchor, setPerfilAnchor] = useState(null);

  const handleLogout = () => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('userInfo');
    permissionService.clearCache();
    if (onLogout) onLogout();
    navigate('/login');
  };

  const handleAdministracionClick = (event) => {
    setAdministracionAnchor(event.currentTarget);
  };

  const handleAdministracionClose = () => {
    setAdministracionAnchor(null);
  };

  const handleCotizacionesClick = (event) => {
    setCotizacionesAnchor(event.currentTarget);
  };

  const handleCotizacionesClose = () => {
    setCotizacionesAnchor(null);
  };

  const handleServiciosClick = (event) => {
    setServiciosAnchor(event.currentTarget);
  };

  const handleServiciosClose = () => {
    setServiciosAnchor(null);
  };

  const handleFinanzasClick = (event) => {
    setFinanzasAnchor(event.currentTarget);
  };

  const handleFinanzasClose = () => {
    setFinanzasAnchor(null);
  };

  const handleUtileriasClick = (event) => {
    setUtileriasAnchor(event.currentTarget);
  };

  const handleUtileriasClose = () => {
    setUtileriasAnchor(null);
  };

  const handleUsuariosClick = (event) => {
    setUsuariosAnchor(event.currentTarget);
  };

  const handleUsuariosClose = () => {
    setUsuariosAnchor(null);
  };

  const handleNosotrosClick = (event) => {
    setNosotrosAnchor(event.currentTarget);
  };

  const handleNosotrosClose = () => {
    setNosotrosAnchor(null);
  };

  // Componente helper para elementos de menú protegidos basado en role_screens
  const ProtectedMenuItem = ({ route, children, onClick, bypassPermissions = false }) => {
    console.log(`🔍 ProtectedMenuItem verificando: ${route}`);
    
    // Obtener información del usuario desde sessionStorage
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
    const userRole = userInfo?.role?.name;
    
    console.log(`🔍 DEBUG ProtectedMenuItem para ${route}:`, {
      userInfo: userInfo,
      userRole: userRole,
      roleObject: userInfo?.role,
      bypassPermissions: bypassPermissions
    });
    
    // Bypass para testing - siempre mostrar
    if (bypassPermissions) {
      console.log(`🔓 BYPASS activado para: ${route}`);
      return (
        <MenuItem component={Link} to={route} onClick={onClick}>
          {children}
        </MenuItem>
      );
    }
    
    // EXCEPCIÓN: Si es ADMINISTRADOR, mostrar siempre sin validaciones
    // Verificar múltiples variantes del rol de administrador
    const isAdmin = userRole === 'ADMINISTRADOR' || 
                   userRole === 'ADMIN' || 
                   userRole === 'Administrator' ||
                   userInfo?.role?.id === 1; // También verificar por ID
    
    if (isAdmin) {
      console.log(`👑 Usuario ADMINISTRADOR detectado (${userRole}) - mostrando ${route} sin validaciones`);
      return (
        <MenuItem component={Link} to={route} onClick={onClick}>
          {children}
        </MenuItem>
      );
    }
    
    // Para otros usuarios, aplicar validaciones restrictivas
    if (permissionsLoading) {
      console.log(`⏳ Permisos cargando, OCULTANDO: ${route}`);
      return null;
    }
    
    // Si no hay permisos cargados, NO mostrar (usuario sin configuración)
    if (!permissions || permissions.length === 0) {
      console.log(`❌ Sin permisos configurados, OCULTANDO submenú: ${route}`);
      return null;
    }
    
    // Verificar permisos específicos usando role_screens del backend
    console.log(`🔍 Llamando hasPermission para ${route} con userRole: ${userRole}`);
    const hasAccess = hasPermission(route, 'read');
    console.log(`🔐 Permiso para ${route}: ${hasAccess ? '✅ PERMITIDO' : '❌ DENEGADO'} (userRole: ${userRole}, isAdmin: ${isAdmin})`);
    
    if (!hasAccess) {
      console.log(`🔒 OCULTANDO submenú sin acceso: ${route}`);
      return null;
    }
    
    console.log(`✅ Mostrando submenú con acceso confirmado: ${route}`);
    return (
      <MenuItem component={Link} to={route} onClick={onClick}>
        {children}
      </MenuItem>
    );
  };

  // Función para verificar si el usuario es tipo INVITADO (no admin)
  // role puede ser string ("ADMINISTRADOR") u objeto ({id, name})
  const isGuestUser = () => {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
    const role = userInfo?.role;
    const userRoleName = typeof role === 'string' ? role : role?.name;
    const adminRoles = ['ADMINISTRADOR', 'ADMIN', 'ADMINISTRATOR', 'VENTAS',
                        'COORDINADOR_OPERACIONES', 'EJECUTIVO_OPERACIONES', 'SUPERVISOR_CALIDAD'];
    const isAdmin = adminRoles.includes(userRoleName?.toUpperCase()) ||
                   (typeof role === 'object' && role?.id === 1);
    return !isAdmin;
  };

  // Funciones de programas ya no necesarias - simplificado a menú plano
  // const handleProgramasClick = (event) => {
  //   setProgramasAnchor(event.currentTarget);
  // };

  // const handleProgramasClose = () => {
  //   setProgramasAnchor(null);
  //   handleServiciosClose();
  // };

  const handleClientesClick = (event) => {
    setClientesAnchor(event.currentTarget);
  };

  const handleClientesClose = () => {
    setClientesAnchor(null);
  };

  const handlePerfilClick = (event) => {
    setPerfilAnchor(event.currentTarget);
  };

  const handlePerfilClose = () => {
    setPerfilAnchor(null);
  };

  // Función helper para verificar acceso a menús basado en role_screens del backend
  const hasMenuAccess = (routes) => {
    console.log('🔍 Verificando acceso a menú con rutas:', routes);
    
    // Obtener información del usuario desde sessionStorage
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
    const userRole = userInfo?.role?.name;
    
    console.log('📊 Estado de permisos:', {
      permissionsLoading,
      permissionsCount: permissions?.length || 0,
      userRole: userRole,
      hasPermissionFunc: typeof hasPermission
    });
    
    // EXCEPCIÓN: Si es ADMINISTRADOR, dar acceso completo sin validaciones
    // Verificar múltiples variantes del rol de administrador
    const isAdmin = userRole === 'ADMINISTRADOR' || 
                   userRole === 'ADMIN' || 
                   userRole === 'Administrator' ||
                   userInfo?.role?.id === 1; // También verificar por ID
    
    if (isAdmin) {
      console.log(`👑 Usuario ADMINISTRADOR detectado (${userRole}) - acceso completo sin validaciones`);
      return true;
    }
    
    // Para otros usuarios, aplicar validaciones restrictivas
    if (permissionsLoading) {
      console.log(`🔄 Permisos cargando - ocultando menús temporalmente`);
      return false;
    }
    
    if (!permissions || permissions.length === 0) {
      console.log(`❌ No hay permisos - ocultando todos los menús`);
      return false;
    }
    
    const hasAccessToAnyRoute = routes.some(route => hasPermission(route, 'read'));
    console.log(`🔐 hasMenuAccess para rutas [${routes.join(', ')}]: ${hasAccessToAnyRoute}`);
    return hasAccessToAnyRoute;
  };
  // Configuración simple y estándar para todos los menús
  const menuProps = {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null, // Fuerza usar anchorOrigin
  };

  return (
    <AppBar position="static" sx={{ mb: 2, zIndex: 1300 }}>
      <Toolbar sx={{ position: 'relative' }}>
        {/* Logo de Conem en la parte izquierda superior */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <IconButton component={Link} to="/home" edge="start" sx={{ p: 0, mr: 1 }}>
            <img src="/logo.ico" alt="Conem Logo" style={{ height: 40 }} />
          </IconButton>
          <Typography
            variant="h6"
            component={Link}
            to="/home"
            sx={{ textDecoration: 'none', color: 'inherit', fontWeight: 'bold', mr: 1 }}
          >
            CONEM
          </Typography>
          {conemVersion && (
            <Chip
              label={`v${conemVersion}`}
              size="small"
              sx={{ mr: 4, height: 20, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.2)', color: 'inherit' }}
            />
          )}
        </Box>
        {/* Menús flotantes */}
        <Box sx={{ flexGrow: 1 }}>
          <Button
            color="inherit"
            onClick={handleAdministracionClick}
            endIcon={<ExpandMore />}
            sx={{ mr: 2 }}
          >
            Administración
          </Button>
          <Menu
            anchorEl={administracionAnchor}
            open={Boolean(administracionAnchor)}
            onClose={handleAdministracionClose}
            MenuListProps={{ 'aria-labelledby': 'administracion-button' }}
            {...menuProps}
          >
            <MenuItem component={Link} to="/home" onClick={handleAdministracionClose}>
              Dashboard
            </MenuItem>
            {hasMenuAccess(['/tablero-seguimiento']) && (
              <MenuItem component={Link} to="/tablero-seguimiento" onClick={handleAdministracionClose}>
                Tablero
              </MenuItem>
            )}
          </Menu>
          {hasMenuAccess(['/clientes/empresas', '/clientes/sucursales']) && (
            <Button 
              color="inherit" 
              onClick={handleClientesClick}
              endIcon={<ExpandMore />}
              sx={{ mr: 2 }}
            >
              Prospectos
            </Button>
          )}
          <Menu
            anchorEl={clientesAnchor}
            open={Boolean(clientesAnchor)}
            onClose={handleClientesClose}
            MenuListProps={{
              'aria-labelledby': 'clientes-button',
            }}
            {...menuProps}
          >
            <ProtectedMenuItem route="/clientes/empresas" onClick={handleClientesClose}>
              Prospectos
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/clientes/sucursales" onClick={handleClientesClose}>
              Gestión de Sucursales
            </ProtectedMenuItem>
          </Menu>
          {hasMenuAccess(['/new-quotation', '/estadisticas']) && (
            <Button 
              color="inherit" 
              onClick={handleCotizacionesClick}
              endIcon={<ExpandMore />}
              sx={{ mr: 2 }}
            >
              Cotizaciones
            </Button>
          )}
          <Menu
            anchorEl={cotizacionesAnchor}
            open={Boolean(cotizacionesAnchor)}
            onClose={handleCotizacionesClose}
            MenuListProps={{
              'aria-labelledby': 'cotizaciones-button',
            }}
            {...menuProps}
          >
            <ProtectedMenuItem route="/cotizaciones/new-quotation" onClick={handleCotizacionesClose}>
              Nueva
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/estadisticas" onClick={handleCotizacionesClose}>
              Gestión
            </ProtectedMenuItem>
          </Menu>
          {hasMenuAccess(['/servicios/catalogo', '/servicios/agenda', '/servicios/ordenes-trabajo', '/servicios/programas', '/servicios/capacitaciones', '/servicios/revision-calidad']) && (
            <Button 
              color="inherit" 
              onClick={handleServiciosClick}
              endIcon={<ExpandMore />}
              sx={{ mr: 2 }}
            >
              Servicios
            </Button>
          )}
          <Menu
            anchorEl={serviciosAnchor}
            open={Boolean(serviciosAnchor)}
            onClose={handleServiciosClose}
            MenuListProps={{
              'aria-labelledby': 'servicios-button',
            }}
            {...menuProps}
          >
            <ProtectedMenuItem route="/servicios/catalogo" onClick={handleServiciosClose}>
              Catálogo de servicios
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/servicios/programas" onClick={handleServiciosClose}>
              Programas
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/servicios/agenda" onClick={handleServiciosClose}>
              Agenda de Servicios
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/servicios/ordenes-trabajo" onClick={handleServiciosClose}>
              Órdenes de Trabajo
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/servicios/documentos" onClick={handleServiciosClose}>
              Gestión Documental
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/servicios/capacitaciones" onClick={handleServiciosClose}>
              Capacitaciones
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/servicios/revision-calidad" onClick={handleServiciosClose}>
              Revisión de Calidad
            </ProtectedMenuItem>
          </Menu>
          {hasMenuAccess(['/usuarios/catalogo', '/usuarios/perfiles', '/usuarios/analitica', '/usuarios/roles']) && (
            <Button 
              color="inherit" 
              onClick={handleUsuariosClick}
              endIcon={<ExpandMore />}
              sx={{ mr: 2 }}
            >
              Usuarios
            </Button>
          )}
          <Menu
            anchorEl={usuariosAnchor}
            open={Boolean(usuariosAnchor)}
            onClose={handleUsuariosClose}
            MenuListProps={{
              'aria-labelledby': 'usuarios-button',
            }}
            {...menuProps}
          >
            <ProtectedMenuItem route="/usuarios/catalogo" onClick={handleUsuariosClose}>
              Catálogo de usuarios
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/usuarios/perfiles" onClick={handleUsuariosClose}>
              Perfiles detallados
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/usuarios/analitica" onClick={handleUsuariosClose}>
              Analítica de usuarios
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/usuarios/roles" onClick={handleUsuariosClose}>
              Gestión de roles
            </ProtectedMenuItem>
          </Menu>
          {hasMenuAccess(['/finanzas/abonos']) && (
            <Button 
              color="inherit" 
              onClick={handleFinanzasClick}
              endIcon={<ExpandMore />}
              sx={{ mr: 2 }}
            >
              Finanzas
            </Button>
          )}
          <Menu
            anchorEl={finanzasAnchor}
            open={Boolean(finanzasAnchor)}
            onClose={handleFinanzasClose}
            MenuListProps={{
              'aria-labelledby': 'finanzas-button',
            }}
            {...menuProps}
          >
            <ProtectedMenuItem route="/finanzas/abonos" onClick={handleFinanzasClose}>
              Gestión de Abonos
            </ProtectedMenuItem>
          </Menu>
          {isGuestUser() && hasMenuAccess(['/profile', '/perfil-riesgo/llenar-informacion', '/perfil-riesgo/subir-archivos', '/perfil-riesgo/historial']) && (
            <Button 
              color="inherit" 
              onClick={handlePerfilClick}
              endIcon={<ExpandMore />}
              sx={{ mr: 2 }}
            >
              <Badge color="warning" badgeContent={pendingDeliveries || null} sx={{ '& .MuiBadge-badge': { right: -10 } }}>
                Mi Perfil de Riesgo
              </Badge>
            </Button>
          )}
          <Menu
            anchorEl={perfilAnchor}
            open={Boolean(perfilAnchor)}
            onClose={handlePerfilClose}
            MenuListProps={{
              'aria-labelledby': 'perfil-button',
            }}
            {...menuProps}
          >
            <ProtectedMenuItem route="/profile" onClick={handlePerfilClose}>
              Mi Perfil de Usuario
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/perfil-riesgo/historial" onClick={handlePerfilClose}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>Cotizaciones</span>
                {hasAcceptedQuotation && <Chip label="✓" size="small" sx={{ height: 20, bgcolor: '#43e97b', color: 'white', fontWeight: 'bold' }} />}
              </Box>
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/perfil-riesgo/subir-archivos" onClick={handlePerfilClose}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>Subir Archivos</span>
                {hasUploadedFiles && <Chip label="✓" size="small" sx={{ height: 20, bgcolor: '#43e97b', color: 'white', fontWeight: 'bold' }} />}
              </Box>
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/perfil-riesgo/mis-documentos" onClick={handlePerfilClose} bypassPermissions>
              Mis documentos entregados
              {pendingDeliveries > 0 && (
                <Chip label={`${pendingDeliveries} por confirmar`} size="small" color="warning" sx={{ ml: 1, height: 20, fontWeight: 700 }} />
              )}
            </ProtectedMenuItem>
          </Menu>
          {hasMenuAccess(['/utilerias/correos', '/utilerias/dropbox', '/utilerias/sustancias-costo']) && (
            <Button 
              color="inherit" 
              onClick={handleUtileriasClick}
              endIcon={<ExpandMore />}
              sx={{ mr: 2 }}
            >
              Utilerías
            </Button>
          )}
          <Menu
            anchorEl={utileriasAnchor}
            open={Boolean(utileriasAnchor)}
            onClose={handleUtileriasClose}
            MenuListProps={{
              'aria-labelledby': 'utilerias-button',
            }}
            {...menuProps}
          >
            <ProtectedMenuItem route="/utilerias/correos" onClick={handleUtileriasClose}>
              Configuración de correos y notificaciones
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/utilerias/dropbox" onClick={handleUtileriasClose}>
              Configuración de Dropbox
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/utilerias/sustancias-costo" onClick={handleUtileriasClose}>
              Costos por Sustancias Peligrosas
            </ProtectedMenuItem>
          </Menu>
          {hasMenuAccess(['/nosotros/empresa', '/nosotros/mision-vision', '/nosotros/equipo', '/nosotros/historia', '/nosotros/contacto']) && (
            <Button 
              color="inherit" 
              onClick={handleNosotrosClick}
              endIcon={<ExpandMore />}
              sx={{ mr: 2 }}
            >
              Nosotros
            </Button>
          )}
          <Menu
            anchorEl={nosotrosAnchor}
            open={Boolean(nosotrosAnchor)}
            onClose={handleNosotrosClose}
            MenuListProps={{
              'aria-labelledby': 'nosotros-button',
            }}
            {...menuProps}
          >
            <ProtectedMenuItem route="/nosotros/empresa" onClick={handleNosotrosClose}>
              Nuestra Empresa
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/nosotros/mision-vision" onClick={handleNosotrosClose}>
              Misión y Visión
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/nosotros/equipo" onClick={handleNosotrosClose}>
              Nuestro Equipo
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/nosotros/historia" onClick={handleNosotrosClose}>
              Historia
            </ProtectedMenuItem>
            <ProtectedMenuItem route="/nosotros/contacto" onClick={handleNosotrosClose}>
              Contacto
            </ProtectedMenuItem>
          </Menu>
        </Box>
        {OFFLINE_FIELD_MODE && <OfflineSyncIndicator />}
        {/* Notificaciones no leídas */}
        <IconButton color="inherit" component={Link} to="/home" sx={{ mr: 1 }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
        {/* Usuario conectado */}
        <Button
          color="inherit"
          onClick={handleUserMenuOpen}
          startIcon={
            <Avatar sx={{ width: 30, height: 30, bgcolor: 'rgba(255,255,255,0.25)', fontSize: '0.85rem' }}>
              {userInitials}
            </Avatar>
          }
          endIcon={<ExpandMore />}
          sx={{ textTransform: 'none', ml: 1 }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15 }}>
            <Typography component="span" sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'inherit', lineHeight: 1.2 }}>
              {userDisplayName}
            </Typography>
            {userRoleName && (
              <Typography component="span" sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.2 }}>
                {userRoleName}
              </Typography>
            )}
            {currentUserInfo.email && (
              <Typography component="span" sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.2 }}>
                {currentUserInfo.email}
              </Typography>
            )}
          </Box>
        </Button>
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          {...menuProps}
        >
          <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {userDisplayName}
            </Typography>
            {userRoleName && (
              <Typography variant="caption" color="text.secondary">
                {userRoleName}
              </Typography>
            )}
            {currentUserInfo.email && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {currentUserInfo.email}
              </Typography>
            )}
          </Box>
          <Divider />
          <MenuItem onClick={() => { handleUserMenuClose(); navigate('/profile'); }}>
            <ListItemIcon>
              <AccountCircle fontSize="small" />
            </ListItemIcon>
            <ListItemText>Mi perfil</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cerrar sesión</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;